import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import useDataFetcher from '~/components/useDataFetcher';
import type { OutgoingPromotionDetail } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import ChangeListByType from './ChangeListByType';
import PromotionEnvBadge from './PromotionEnvBadge';
import PromotionStatusChip from './PromotionStatusChip';
import { isTargetUnreachableError, promotionsBaseRoute, TARGET_UNREACHABLE_MESSAGE } from './promotionsUtils';
import type { PromotionChange } from './types';
import { PROMOTION_STATUS } from './types';

interface OutgoingPromotionDetailDialogProps {
  requestUuid: string | null;
  organizationId: number;
  onClose: () => void;
  onChanged: () => void;
}

/** Source-side detail: the outgoing promotion + the source's own copy of the change list. The source
 * may withdraw (cancel) a still-pending request. */
const OutgoingPromotionDetailDialog: React.FC<OutgoingPromotionDetailDialogProps> = ({
  requestUuid,
  organizationId,
  onClose,
  onChanged,
}) => {
  const baseRoute = promotionsBaseRoute(organizationId);
  const canWrite = useHasPermission({
    action: PERMISSION_ACTIONS.SYSCONFIG_CONFIG_MANAGEMENT,
    verb: PERMISSION_VERBS.WRITE,
  });
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelReasonDirty, setIsCancelReasonDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The cross-deployment cancel reached an unreachable target (502): non-blocking, the user can retry.
  const [cancelUnreachable, setCancelUnreachable] = useState(false);

  // The dialog is reused for every outgoing request; clear the stale notice when the request changes so
  // it can't leak onto a different promotion.
  React.useEffect(() => {
    setCancelUnreachable(false);
  }, [requestUuid]);

  const { isLoading, isError, data } = useDataFetcher<OutgoingPromotionDetail>(
    requestUuid ? `${baseRoute}/outgoing/${requestUuid}` : null,
    null,
    { shouldFetch: Boolean(requestUuid) }
  );

  const handleCancel = async () => {
    if (!requestUuid || !cancelReason.trim()) {
      return;
    }
    try {
      setIsSubmitting(true);
      setCancelUnreachable(false);
      await axios.post(`${baseRoute}/cancel`, { request_uuid: requestUuid, reason: cancelReason.trim() });
      setIsCancelConfirmOpen(false);
      setCancelReason('');
      setIsCancelReasonDirty(false);
      onChanged();
      onClose();
    } catch (error) {
      if (isTargetUnreachableError(error)) {
        // Target deployment unreachable — close the confirm and show a non-blocking notice; the user can
        // re-open and retry once the target is back. Not a bug → no error modal / Sentry.
        setIsCancelConfirmOpen(false);
        setCancelUnreachable(true);
      } else {
        await reportAxiosError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPending = data?.cached_status === PROMOTION_STATUS.PENDING;

  return (
    <CardDialog
      isDialog
      title="Outgoing promotion"
      open={Boolean(requestUuid)}
      onClose={onClose}
      preventClose={isSubmitting}
      fullWidth
      maxWidth="md"
      footerActions={
        <>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
          {isPending && canWrite && (
            <Button
              variant="contained"
              color="secondary"
              disabled={isSubmitting}
              onClick={() => setIsCancelConfirmOpen(true)}
            >
              Cancel promotion
            </Button>
          )}
        </>
      }
    >
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        {data && (
          <div className="flex flex-col gap-16 py-8">
            <div className="text-lg font-bold">{data.request_name || '—'}</div>
            <div className="flex items-center gap-x-12">
              <span>
                To <span className="font-bold">{data.target_org_external_id}</span>
              </span>
              <PromotionEnvBadge subDomain={data.target_deployment_id} />
              <PromotionStatusChip status={data.cached_status} />
            </div>

            {cancelUnreachable && (
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.WARNING}
                title="Target environment unreachable"
                note={TARGET_UNREACHABLE_MESSAGE}
              />
            )}

            {data.cached_reject_reason && (
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.INFO}
                title={data.cached_status === PROMOTION_STATUS.CANCELLED ? 'Cancellation reason' : 'Rejection reason'}
                note={data.cached_reject_reason}
              />
            )}

            <ChangeListByType changes={data.changes as PromotionChange[]} summary={data.summary} />
          </div>
        )}
      </LoadingSwitch>

      <ConfirmModal
        isOpen={isCancelConfirmOpen}
        isSubmitting={isSubmitting}
        onClose={() => {
          setIsCancelConfirmOpen(false);
          setCancelReason('');
          setIsCancelReasonDirty(false);
        }}
        title="Cancel promotion"
        contentText={
          <div className="flex flex-col gap-12">
            <span>Withdraw this pending promotion request? The target will no longer see it for review.</span>
            <TextField
              label="Cancellation reason"
              size="medium"
              value={cancelReason}
              onChange={(newValue: string) => {
                setCancelReason(newValue);
                setIsCancelReasonDirty(true);
              }}
              error={isCancelReasonDirty && !cancelReason.trim()}
              helperText={isCancelReasonDirty && !cancelReason.trim() ? 'A reason is required' : ''}
              multiline
              rows={3}
              fullWidth
              autoFocus
            />
          </div>
        }
        primaryButtonName="Cancel promotion"
        secondaryButtonName="Keep"
        onPrimaryBtnClick={handleCancel}
      />
    </CardDialog>
  );
};

export default OutgoingPromotionDetailDialog;
